import {
  Article,
  ArticleImage,
  ArticleList,
  ArticleSubtitle,
  ArticleTitle
} from "@babackapp/customer-ui/components/article";
import { SectionHeader } from "@babackapp/customer-ui/components/header";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useReturnFinancials } from "@babackapp/return-widget-components/hooks/use-return-financials";
import { FinancialRecap } from "@babackapp/return-widget-components/components/financial-recap";
import { usePublicOrder } from "@babackapp/return-widget-components/queries/use-public-order";
import { usePublicShop } from "@babackapp/return-widget-components/queries/use-public-shop";
import { formatPrice } from "@babackapp/return-widget-components/utils/format-price";
import type { CartItem } from "@babackapp/return-widget-components/types/shop-now";
import { Separator } from "@babackapp/ui/components/separator";
import { CompleteReturnLineItem } from "./complete-return-line-item";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useWidgetParams } from "../hooks/use-widget-params";
import { RebuyReceipt } from "./complete-rebuy-receipt";
import { useProductReferencesVisibility } from "../hooks/use-show-product-references";

type Props = {
  orderId: string;
  cart: { items: CartItem[]; currency: string } | null;
};

export function CompleteSummary({ orderId, cart }: Props) {
  const { shopId } = useWidgetParams();
  const { data: order } = usePublicOrder(orderId);
  const { data: shop } = usePublicShop(shopId);
  const draft = useDraftReturn(orderId);
  const financials = useReturnFinancials(orderId, shopId, order ?? null);
  const financialSummary = financials.summary;
  const t = useCustomText();
  const references = useProductReferencesVisibility(shopId);
  const referencesHint = references.isMerchantOnly
    ? t("misc.connectedUserData")
    : undefined;

  const returnItems = draft?.lineItems ?? [];
  const exchangeItems = returnItems.filter(
    (li) => li.resolution === "exchange" || li.resolution === "exchange-credit",
  );
  const refundItems = returnItems.filter((li) => li.resolution === "refund");
  const discountItems = returnItems.filter((li) => li.resolution === "discount");
  const orderLineItems = order?.lineItems ?? [];

  return (
    <div className="w-full mt-4 flex flex-col gap-6">
      {exchangeItems.length > 0 && (
        <SummarySection title={t("summary.exchangeItems")}>
          {exchangeItems.map((item) => (
            <CompleteReturnLineItem
              key={item.lineItemId}
              item={item}
              orderLineItems={orderLineItems}
              order={order ?? null}
              currency={financialSummary.currency}
              showReferences={references.shouldShow}
              referencesHint={referencesHint}
            />
          ))}
        </SummarySection>
      )}
      {refundItems.length > 0 && (
        <SummarySection title={t("summary.refundItems")}>
          {refundItems.map((item) => (
            <CompleteReturnLineItem
              key={item.lineItemId}
              item={item}
              orderLineItems={orderLineItems}
              order={order ?? null}
              currency={financialSummary.currency}
              showReferences={references.shouldShow}
              referencesHint={referencesHint}
            />
          ))}
        </SummarySection>
      )}
      {discountItems.length > 0 && (
        <SummarySection title={t("summary.discountItems")}>
          {discountItems.map((item) => (
            <CompleteReturnLineItem
              key={item.lineItemId}
              item={item}
              orderLineItems={orderLineItems}
              order={order ?? null}
              currency={financialSummary.currency}
              showReferences={references.shouldShow}
              referencesHint={referencesHint}
            />
          ))}
        </SummarySection>
      )}
      {cart && cart.items.length > 0 && (
        <SummarySection title={t("completeSummary.newItems")}>
          {cart.items.map((item, i) => (
            <Article key={`${item.variantId}-${i}`}>
              {item.image && (
                <ArticleImage src={item.image} layoutId={`img-shop-now-complete-${item.variantId}`} />
              )}
              <ArticleTitle>{item.title}</ArticleTitle>
              <ArticleSubtitle>
                ×{item.quantity} · {formatPrice(item.price * item.quantity * financialSummary.taxMultiplier, cart.currency)}
              </ArticleSubtitle>
            </Article>
          ))}
        </SummarySection>
      )}

      <Separator />

      {financialSummary.rebuyTotal > 0 ? (
        <RebuyReceipt financials={financials} t={t} />
      ) : (
        <FinancialRecap
          financials={financials}
          t={t}
          bonusCurrency={
            shop?.discount?.discount_bonus?.currency ?? financialSummary.currency
          }
        />
      )}
    </div>
  );
}

function SummarySection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <SectionHeader>{title}</SectionHeader>
      <ArticleList size="sm">{children}</ArticleList>
    </div>
  );
}
